/**
 * Varianten Set — PDP component for bundle carrier products.
 *
 * - Renders slot cards with inline color swatches (horizontal scroll)
 * - Adds carrier product to cart with _slot_X line attributes
 * - Cart Transform function expands into individual items server-side
 */

if (!customElements.get('varianten-set')) {
  class VariantenSet extends HTMLElement {
    constructor() {
      super();
      this.carrierVariantId = this.dataset.carrierVariantId;
      this.slots = this.querySelectorAll('.varianten-set__slot');
      this.atcButton = this.querySelector('[data-varianten-set-atc]');
      this.bisWrapper = this.querySelector('[data-varianten-set-bis]');
      this.bisModal = this.querySelector('[data-bis-modal]');
      this.bisSelection = null;
      this.bisAuto = [];
      this.init();
    }

    init() {
      this.setupSwatches();
      this.setupArrowNav();
      this.setupDragScroll();
      this.setupAtc();
      this.updateAvailability();
      this.setupStellaRecommendations();
      this.setupGalleryTriggers();
      this.setupGalleryClickSelect();
      this.setupBackInStock();
    }

    // Set option "Galerie zeigt die gewaehlten Farben": the slot image is clickable, so products
    // without color choice can show their images too
    setupGalleryTriggers() {
      if (this.dataset.slotGallery !== 'true') return;
      this.slots.forEach((slot) => {
        const trigger = slot.querySelector('[data-slot-gallery-trigger]');
        if (!trigger) return;
        const open = () => this.renderSlotGallery(slot);
        trigger.addEventListener('click', open);
        trigger.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            open();
          }
        });
      });
    }

    // --- DRAG-TO-SCROLL on the swatches track (mouse only).
    // Threshold of 10px ensures small click-jitter doesn't trigger drag-mode
    // (so swatch clicks still register normally). ---
    setupDragScroll() {
      const DRAG_THRESHOLD = 10;
      this.querySelectorAll('.varianten-set__swatches').forEach((track) => {
        let isDown = false;
        let startX = 0;
        let startScroll = 0;
        let hasDragged = false;

        track.addEventListener('mousedown', (e) => {
          if (e.button !== 0) return;
          isDown = true;
          hasDragged = false;
          startX = e.pageX;
          startScroll = track.scrollLeft;
        });

        const endDrag = () => {
          isDown = false;
          track.style.cursor = '';
        };
        track.addEventListener('mouseleave', endDrag);
        track.addEventListener('mouseup', endDrag);

        track.addEventListener('mousemove', (e) => {
          if (!isDown) return;
          const walk = e.pageX - startX;
          if (!hasDragged && Math.abs(walk) > DRAG_THRESHOLD) {
            hasDragged = true;
            track.style.cursor = 'grabbing';
          }
          if (hasDragged) {
            e.preventDefault();
            track.scrollLeft = startScroll - walk;
          }
        });

        // Only block swatch click after a real drag — single clicks fall through.
        track.addEventListener('click', (e) => {
          if (hasDragged) {
            e.stopPropagation();
            e.preventDefault();
            hasDragged = false;
          }
        }, true);
      });
    }

    // --- STELLA RECOMMENDATIONS (analog assets/variants.js) ---
    setupStellaRecommendations() {
      // Initial check from localStorage
      this.checkStellaRecommended();

      // Set Creator popup variant
      document.addEventListener('stella-analysis-finished', (event) => {
        this.resetStellaRecommendations();
        if (event.detail && event.detail.products) {
          this.showStellaRecommended(event.detail.products.map((p) => String(p.variant_id)));
        } else {
          this.checkStellaRecommended();
        }
      });

      // PDP iframe variant
      window.addEventListener('message', (event) => {
        const domainPattern = /^(https:\/\/)?([a-zA-Z0-9-]+\.)*(askstella\..*|woowclub\..*|jblstage\.wpengine\.com)$/;
        if (!domainPattern.test(event.origin)) return;
        if (event.data && (event.data.type === 'analyse_finished' || event.data.type === 'analysis_finished' || event.data.type === 'analyseFinished')) {
          this.resetStellaRecommendations();
          if (event.data.products && Array.isArray(event.data.products)) {
            this.showStellaRecommended(event.data.products.map((p) => String(p.variant_id)));
          } else {
            this.checkStellaRecommended();
          }
        }
      });
    }

    resetStellaRecommendations() {
      this.querySelectorAll('.options__label--recommended').forEach((label) => {
        label.style.display = 'none';
        label.classList.remove('force-hover');
        label.querySelectorAll('img').forEach((img) => (img.style.display = 'none'));
      });
    }

    checkStellaRecommended() {
      const stellaData = localStorage.getItem('askStellaAnalysisData');
      const stellaRecommendedVariants = localStorage.getItem('stellaRecommendedProductVariants');
      if (!stellaData && !stellaRecommendedVariants) return;

      try {
        let recommendedVariantIds = [];
        if (stellaData) {
          let parsedData = JSON.parse(stellaData);
          if (Array.isArray(parsedData)) {
            parsedData = parsedData[parsedData.length - 1];
          }
          if (parsedData.products && Array.isArray(parsedData.products)) {
            recommendedVariantIds = parsedData.products.map((p) => p.variant_id);
          }
        }
        if (recommendedVariantIds.length === 0 && stellaRecommendedVariants) {
          const parsedRecommendations = JSON.parse(stellaRecommendedVariants);
          if (Array.isArray(parsedRecommendations) && parsedRecommendations.length > 0) {
            const latestTimestamp = Math.max(...parsedRecommendations.map((r) => r.timestamp));
            recommendedVariantIds = parsedRecommendations
              .filter((r) => r.timestamp === latestTimestamp)
              .map((r) => r.variant_id)
              .filter(Boolean);
          }
        }
        if (recommendedVariantIds.length === 0) return;
        this.showStellaRecommended(recommendedVariantIds);
      } catch (error) {
        console.error('Varianten-Set Stella parse error:', error);
      }
    }

    // Preselect first Stella-recommended variant per slot (only if available)
    preselectStellaVariants(recommendedVariantIds) {
      const recIds = recommendedVariantIds.map(String);
      this.slots.forEach((slot) => {
        if (slot.dataset.slotHidden === 'true') return;
        let variants = [];
        try {
          variants = JSON.parse(slot.dataset.variants || '[]');
        } catch {
          variants = [];
        }
        const recVariant = variants.find((v) => recIds.includes(String(v.id)) && v.available);
        if (recVariant && String(slot.dataset.selectedVariantId) !== String(recVariant.id)) {
          this.selectVariant(slot, recVariant.id);
        }
      });

      // Gallery on load follows the preselected colors (set option "Galerie zeigt die gewaehlten Farben")
      if (this.dataset.slotGallery === 'true' && this.galleryReady) {
        this.renderSlotGallery(null, { initial: true });
      }
    }

    showStellaRecommended(recommendedVariantIds) {
      // Preselect the recommended variants in the slots
      this.preselectStellaVariants(recommendedVariantIds);

      const getTextColorForBackground = (hexColor) => {
        if (!hexColor || hexColor.length < 7) return 'dark';
        const r = parseInt(hexColor.substring(1, 3), 16);
        const g = parseInt(hexColor.substring(3, 5), 16);
        const b = parseInt(hexColor.substring(5, 7), 16);
        const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
        return brightness > 128 ? 'dark' : 'light';
      };

      const isInViewport = (el) => {
        const rect = el.getBoundingClientRect();
        return (
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
          rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
      };

      const recommendedLabels = this.querySelectorAll('.options__label--recommended');
      let foundFirst = false;

      recommendedLabels.forEach((label) => {
        const variantId = label.dataset.variantId;
        const colorHex = label.dataset.colorHex || '#000000';
        const iconType = getTextColorForBackground(colorHex);
        const isRecommended = recommendedVariantIds.map(String).includes(variantId);

        if (!isRecommended) return;

        label.style.display = 'flex';
        const darkIcon = label.querySelector('img[src*="icon-star.png"]');
        const whiteIcon = label.querySelector('img[src*="icon-star-white.png"]');
        if (iconType === 'dark') {
          if (darkIcon) darkIcon.style.display = 'block';
          if (whiteIcon) whiteIcon.style.display = 'none';
        } else {
          if (whiteIcon) whiteIcon.style.display = 'block';
          if (darkIcon) darkIcon.style.display = 'none';
        }

        let hoverTimer;
        label.addEventListener('mouseenter', () => {
          hoverTimer = setTimeout(() => label.classList.add('force-hover'), 1000);
        });
        label.addEventListener('mouseleave', () => {
          clearTimeout(hoverTimer);
          label.classList.remove('force-hover');
        });

        if (!foundFirst) {
          const showForceHover = () => {
            label.classList.add('force-hover');
            foundFirst = true;
            setTimeout(() => label.classList.remove('force-hover'), 4000);
          };
          if (isInViewport(label)) {
            showForceHover();
          } else {
            const observer = new IntersectionObserver((entries, obs) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  showForceHover();
                  obs.disconnect();
                }
              });
            }, { threshold: 0.1 });
            observer.observe(label);
          }
        }
      });
    }

    // --- AVAILABILITY: bundle is unavailable if any slot has no
    // available selected variant. Recomputed on every variant change. ---
    updateAvailability() {
      if (!this.atcButton) return;

      let bundleAvailable = true;

      this.slots.forEach((slot) => {
        // Slot was already unavailable at render time (no in-stock variants at all)
        if (slot.dataset.slotUnavailable === 'true') {
          bundleAvailable = false;
          return;
        }

        let variants = [];
        try {
          variants = JSON.parse(slot.dataset.variants || '[]');
        } catch {
          variants = [];
        }
        const selectedId = slot.dataset.selectedVariantId;
        const selected = variants.find((v) => String(v.id) === String(selectedId));
        const slotAvailable = Boolean(selected?.available);

        slot.classList.toggle('varianten-set__slot--unavailable', !slotAvailable);

        if (!slotAvailable) bundleAvailable = false;
      });

      this.atcButton.disabled = !bundleAvailable;

      // Delivery note follows the selection (all states are rendered, the attribute picks one)
      const status = this.querySelector('.product-form__inventory-status');
      if (status) {
        if (!status.dataset.initialStatus) status.dataset.initialStatus = status.dataset.inventoryStatus;
        const initial = status.dataset.initialStatus;
        status.dataset.inventoryStatus = bundleAvailable
          ? (initial === 'unavailable' ? 'available' : initial)
          : 'unavailable';
      }

      const textEl = this.atcButton.querySelector('[data-atc-text]');
      if (textEl) {
        textEl.textContent = bundleAvailable
          ? this.atcButton.dataset.labelAtc
          : this.atcButton.dataset.labelSoldOut;
      }
    }

    // --- SWATCH SELECTION ---
    setupSwatches() {
      this.slots.forEach((slot) => {
        const swatches = slot.querySelectorAll('.varianten-set__swatch');
        swatches.forEach((swatch) => {
          swatch.addEventListener('click', () => {
            this.selectVariant(slot, swatch.dataset.variantId, { byUser: true });
          });
        });
      });
    }

    // --- ARROW NAV: chevrons scroll swatches track; visibility based on scroll position ---
    setupArrowNav() {
      this.slots.forEach((slot) => {
        const prev = slot.querySelector('[data-role="prev"]');
        const next = slot.querySelector('[data-role="next"]');
        const track = slot.querySelector('[data-slot-swatches]');
        if (!track) return;

        const updateArrowVisibility = () => {
          const max = track.scrollWidth - track.clientWidth;
          const canScrollLeft = track.scrollLeft > 1;
          const canScrollRight = max > 0 && track.scrollLeft < max - 1;

          if (prev) prev.classList.toggle('varianten-set__slot-arrow--inactive', !canScrollLeft);
          if (next) next.classList.toggle('varianten-set__slot-arrow--inactive', !canScrollRight);

          track.classList.toggle('varianten-set__swatches--fade-left', canScrollLeft);
          track.classList.toggle('varianten-set__swatches--fade-right', canScrollRight);
        };

        if (next) {
          next.addEventListener('click', () => {
            track.scrollBy({ left: track.clientWidth * 0.7, behavior: 'smooth' });
          });
        }
        if (prev) {
          prev.addEventListener('click', () => {
            track.scrollBy({ left: -track.clientWidth * 0.7, behavior: 'smooth' });
          });
        }

        track.addEventListener('scroll', updateArrowVisibility, { passive: true });
        window.addEventListener('resize', updateArrowVisibility);
        updateArrowVisibility();
      });
    }

    // --- SELECT VARIANT ---
    selectVariant(slot, variantId, { byUser = false } = {}) {
      const variants = JSON.parse(slot.dataset.variants || '[]');
      const variant = variants.find((v) => String(v.id) === String(variantId));
      if (!variant) return;

      // Update stored variant ID
      slot.dataset.selectedVariantId = variantId;

      // Update image — drop srcset so browser uses our new src. Also clear srcset
      // on the <picture><source> siblings: a matching <source> (mobile/tablet
      // breakpoints) otherwise overrides img.src, so on <=1024px the thumbnail
      // would keep showing the default variant after a color change.
      const img = slot.querySelector('[data-slot-img]');
      if (img && variant.image) {
        const picture = img.closest('picture');
        if (picture) {
          picture.querySelectorAll('source').forEach((s) => s.removeAttribute('srcset'));
        }
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = variant.image;
      }

      // Update variant name (option1 + optional " - addition" with weight-light span)
      const nameEl = slot.querySelector('[data-slot-variant-name]');
      if (nameEl) {
        if (variant.title === 'Default Title') {
          nameEl.textContent = '';
        } else {
          nameEl.innerHTML = '';
          const opt = variant.option1 || variant.title || '';
          nameEl.appendChild(document.createTextNode(opt));
          if (variant.addition) {
            const sub = document.createElement('span');
            sub.className = 'weight-light';
            sub.textContent = ' - ' + variant.addition;
            nameEl.appendChild(sub);
          }
        }
      }

      // Update active swatch
      const swatches = slot.querySelectorAll('.varianten-set__swatch');
      swatches.forEach((s) => {
        s.classList.toggle(
          'varianten-set__swatch--active',
          String(s.dataset.variantId) === String(variantId)
        );
      });

      // Galerie mitschalten: nur der Slot, der das Seitenprodukt selbst ist
      // (data-drives-gallery aus dem Snippet)
      if (slot.dataset.drivesGallery === 'true') {
        this.syncPdpGallery(variant);
      }

      // Set option "Galerie zeigt die gewaehlten Farben": only a real click switches the
      // gallery. Stella preselect on load keeps the set images as they are.
      if (byUser && this.dataset.slotGallery === 'true') {
        this.renderSlotGallery(slot);
      }

      this.updateAvailability();
      this.updateBackInStock(slot, variant, byUser);
    }

    // --- BACK IN STOCK: only after the customer clicks a sold-out color.
    // Subscribes the real slot variant via the Klaviyo client API, because the
    // carrier product itself is always in stock and Klaviyo's own button never shows. ---
    updateBackInStock(slot, variant, byUser) {
      if (!this.bisWrapper) return;

      if (byUser && !variant.available) {
        this.bisSelection = { slot, variant };
      } else if (this.bisSelection && this.bisSelection.slot === slot) {
        this.bisSelection = null;
      }

      const current = this.bisSelection;
      const stillSelected =
        current && String(current.slot.dataset.selectedVariantId) === String(current.variant.id);
      if (!stillSelected) this.bisSelection = null;

      this.bisWrapper.hidden = !this.bisSelection && !this.bisAuto.length;
    }

    // Sold-out slots the customer can not click: hidden slots and products without a color
    // choice. The set is not buyable because of them, so the button shows right away.
    findUnclickableSoldOut() {
      return Array.from(this.slots)
        .filter((slot) => slot.dataset.slotUnavailable === 'true')
        .map((slot) => {
          let variants = [];
          try {
            variants = JSON.parse(slot.dataset.variants || '[]');
          } catch (e) {
            variants = [];
          }
          if (!variants.length) return null;
          const clickable = slot.dataset.slotHidden !== 'true' && variants.length > 1;
          if (clickable) return null;
          const variant =
            variants.find((v) => String(v.id) === String(slot.dataset.selectedVariantId)) || variants[0];
          return { slot, variant };
        })
        .filter(Boolean);
    }

    bisTargets() {
      return this.bisSelection ? [this.bisSelection] : this.bisAuto;
    }

    setupBackInStock() {
      if (!this.bisWrapper || !this.bisModal) return;

      this.bisAuto = this.findUnclickableSoldOut();
      this.bisWrapper.hidden = !this.bisSelection && !this.bisAuto.length;

      const modal = this.bisModal;
      const form = modal.querySelector('[data-bis-form]');
      const headline = modal.querySelector('[data-bis-headline]');
      const errorEl = modal.querySelector('[data-bis-error]');
      const successEl = modal.querySelector('[data-bis-success]');
      const submit = modal.querySelector('[data-bis-submit]');
      const emailInput = form.querySelector('input[name="email"]');
      const newsletter = form.querySelector('input[name="newsletter"]');
      const companyId = modal.dataset.companyId;
      const listId = modal.dataset.listId;

      const showError = (text) => {
        errorEl.textContent = text;
        errorEl.hidden = false;
      };

      const productsEl = modal.querySelector('[data-bis-products]');
      const bodyEl = modal.querySelector('[data-bis-body]');
      const targetName = ({ slot, variant }) => {
        const variantName = variant.title === 'Default Title' ? '' : variant.option1 || variant.title;
        return [slot.dataset.productTitle, variantName].filter(Boolean).join(' - ');
      };

      this.bisWrapper.querySelector('[data-bis-open]').addEventListener('click', () => {
        const targets = this.bisTargets();
        if (!targets.length) return;
        const multiple = targets.length > 1;
        headline.textContent = multiple ? modal.dataset.setTitle : targetName(targets[0]);
        productsEl.replaceChildren(
          ...(multiple
            ? targets.map((target) => {
                const item = document.createElement('li');
                item.textContent = targetName(target);
                return item;
              })
            : [])
        );
        productsEl.hidden = !multiple;
        if (bodyEl) bodyEl.textContent = multiple ? modal.dataset.bodyMultiple : modal.dataset.bodySingle;
        form.hidden = false;
        successEl.hidden = true;
        errorEl.hidden = true;
        modal.showModal();
        emailInput.focus();
      });

      modal.querySelector('[data-bis-close]').addEventListener('click', () => modal.close());
      modal.addEventListener('click', (event) => {
        if (event.target === modal) modal.close();
      });

      const post = (path, payload) =>
        fetch(`https://a.klaviyo.com/client/${path}/?company_id=${encodeURIComponent(companyId)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2024-06-15' },
          body: JSON.stringify(payload),
        });

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        errorEl.hidden = true;
        const targets = this.bisTargets();
        if (!targets.length) return;

        const email = emailInput.value.trim();
        if (!emailInput.checkValidity() || !email) {
          showError(modal.dataset.errorEmail);
          return;
        }

        const profile = { data: { type: 'profile', attributes: { email } } };
        submit.disabled = true;

        try {
          // One subscription per variant: the Klaviyo endpoint takes exactly one variant
          const responses = await Promise.all(
            targets.map(({ variant }) =>
              post('back-in-stock-subscriptions', {
                data: {
                  type: 'back-in-stock-subscription',
                  attributes: { profile, channels: ['EMAIL'] },
                  relationships: {
                    variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variant.id}` } },
                  },
                },
              })
            )
          );
          const failed = responses.find((response) => !response.ok);
          if (failed) throw new Error(`Klaviyo BIS ${failed.status}`);

          if (newsletter && newsletter.checked && listId) {
            const listResponse = await post('subscriptions', {
              data: {
                type: 'subscription',
                attributes: { profile, custom_source: 'Back in Stock' },
                relationships: { list: { data: { type: 'list', id: listId } } },
              },
            });
            if (!listResponse.ok) console.error('Varianten-Set Klaviyo newsletter', listResponse.status);
          }

          form.hidden = true;
          successEl.hidden = false;
        } catch (error) {
          console.error('Varianten-Set Klaviyo back in stock', error);
          showError(modal.dataset.errorGeneric);
        } finally {
          submit.disabled = false;
        }
      });
    }

    // Schaltet die Produktgalerie auf die gewaehlte Variante um, mit genau
    // den zwei Mechanismen des echten Variant-Pickers (variants.js):
    // 1. 'variant-change'-Event: die Swiper-Galerie (product-media.js) filtert
    //    ihre Slides ueber die Alt-Texte nach option1 und springt auf Slide 1
    // 2. data-media-id-Prepend: die gestapelte Ansicht zieht das Bild der
    //    Variante nach vorn
    // Bewusst auf die eigene Section begrenzt, damit im Quickshop nicht die
    // Galerie einer anderen Section umschaltet.
    syncPdpGallery(variant) {
      const section = this.closest('section') || this.closest('[class*="section-"]');
      const productMedia = section && section.querySelector('product-media');
      if (!productMedia) return;

      // Sicherheitsnetz wie im Original: nur Varianten, die zur Galerie gehoeren
      const idsAttr = productMedia.getAttribute('data-variant-ids') || '';
      const galleryVariantIds = idsAttr.split(',').map(Number);
      if (!galleryVariantIds.includes(Number(variant.id))) return;

      const fire = () => {
        if (variant.option1) {
          document.dispatchEvent(
            new CustomEvent('variant-change', {
              bubbles: true,
              detail: {
                variant: { id: Number(variant.id), option1: variant.option1 },
                sectionElement: section,
              },
            })
          );
        }

        if (variant.media_id) {
          // Wie der echte Picker nur in der eigenen Section suchen, damit bei
          // doppelt vorhandenem Produkt (PDP + Featured-Section) nie die
          // Galerie einer fremden Section umsortiert wird
          const media = section.querySelector(`[data-media-id$="-${variant.media_id}"]`);
          if (media && media.parentElement) {
            const parent = media.parentElement;
            parent.prepend(media);
            window.setTimeout(() => { parent.scroll(0, 0); });
          }
        }
      };

      // Beim Seitenaufbau (z.B. Stella-Preselect) ist product-media meist noch
      // nicht definiert: das Element laedt erst Swiper/Fancybox per async
      // import und haengt seinen variant-change-Listener danach an. Ein sofort
      // gefeuertes Event verpufft, die Galerie bliebe auf Bild 1. Deshalb
      // warten, bis das Element definiert (und damit upgegradet) ist.
      if (customElements.get('product-media')) {
        fire();
      } else {
        customElements.whenDefined('product-media').then(() => {
          requestAnimationFrame(fire);
        });
      }
    }

    // Rebuilds the product gallery from the set product's own images (color click or click on the
    // slot image): images without id tag, plus images tagged with the selected variant id or with
    // the product id of the clicked slot.
    renderSlotGallery(changedSlot, { initial = false } = {}) {
      const section = this.closest('section') || this.closest('[class*="section-"]');
      const productMedia = section && section.querySelector('product-media');
      if (!productMedia) return;
      const main = productMedia.swiperMainInstance;
      const thumbs = productMedia.swiperThumbsInstance;
      if (!main) return;

      // Original set images, captured once before the first rebuild
      if (!this.galleryOriginals) {
        const pick = (selector) => {
          const pool = Array.from(productMedia.querySelectorAll(`${selector} > .swiper-slide`));
          const slides = pool.length
            ? pool
            : Array.from(productMedia.querySelectorAll(`${selector} > .swiper-wrapper > .swiper-slide:not(.swiper-slide-duplicate)`));
          return slides.map((slide) => slide.cloneNode(true));
        };
        this.galleryOriginals = {
          main: pick('[data-product-media]'),
          thumbs: pick('[data-product-media-thumbnails]'),
        };
      }

      // Which ids count: on load (initial) the preselected variant and the product of every
      // visible slot, after a click only the selected variant and product of the clicked slot.
      const sourceSlots = initial
        ? Array.from(this.slots).filter((slot) => slot.dataset.slotHidden !== 'true')
        : [changedSlot];
      const wanted = new Set();
      sourceSlots.forEach((slot) => {
        if (slot.dataset.selectedVariantId) wanted.add(String(slot.dataset.selectedVariantId));
        if (slot.dataset.productId) wanted.add(String(slot.dataset.productId));
      });

      // Set images: without an id tag ("#farbwunsch_<id>") they always stay. With a tag they stay
      // if one of their ids is wanted.
      const matchingIdx = [];
      const defaultIdx = [];
      this.galleryOriginals.main.forEach((slide, i) => {
        const ids = this.variantIdsOfSlide(slide);
        if (ids.length === 0) defaultIdx.push(i);
        else if (ids.some((id) => wanted.has(id))) {
          defaultIdx.push(i);
          matchingIdx.push(defaultIdx.length - 1);
        }
      });

      // Nothing to show (all images tagged, none for this selection): keep all set images
      // instead of an empty gallery.
      if (defaultIdx.length === 0) {
        this.galleryOriginals.main.forEach((_, i) => defaultIdx.push(i));
        matchingIdx.length = 0;
      }

      // On load without anything filtered out (e.g. set without id tags): leave the gallery
      // untouched, no rebuild, no flicker.
      if (initial && defaultIdx.length === this.galleryOriginals.main.length) return;

      const mainSlides = [];
      const thumbSlides = [];

      // First the set images that stay
      defaultIdx.forEach((i) => {
        mainSlides.push(this.galleryOriginals.main[i].cloneNode(true));
        if (this.galleryOriginals.thumbs[i]) thumbSlides.push(this.galleryOriginals.thumbs[i].cloneNode(true));
      });

      // After a click: jump to the first set image tagged for the selection. On load: image 1.
      const jumpIndex = !initial && matchingIdx.length > 0 ? matchingIdx[0] : 0;

      main.removeAllSlides();
      main.appendSlide(mainSlides);
      if (thumbs) {
        thumbs.removeAllSlides();
        thumbs.appendSlide(thumbSlides);
        thumbs.update();
      }
      main.update();
      main.slideToLoop(jumpIndex, 0);
      if (thumbs) thumbs.slideTo(jumpIndex, 0);
      // Slider icon overlays (pdp_image_slider_image_1), like the gallery's own rebuild does
      if (typeof productMedia.renderProductImageIcons === 'function') productMedia.renderProductImageIcons();

      // Mark the slot whose images are shown (none on load)
      this.slots.forEach((slot) => slot.classList.toggle('varianten-set__slot--gallery-active', !initial && slot === changedSlot));
    }

    // Ids from an image alt text: "#farbwunsch_<variant id or product id>", several separated by
    // commas. Name tags like "#farbwunsch_n-1" are not ids and are ignored here.
    variantIdsOfSlide(slide) {
      const media = slide && slide.querySelector('img, video');
      const alt = (media && media.getAttribute('alt')) || '';
      return Array.from(alt.matchAll(/#farbwunsch_(\d{6,})/gi)).map((m) => m[1]);
    }

    // Click (not swipe) on a gallery image with an id tag: a variant id selects that variant in the
    // box, a product id marks that product's slot. The gallery itself is left as it is.
    setupGalleryClickSelect() {
      if (this.dataset.slotGallery !== 'true') return;
      const section = this.closest('section') || this.closest('[class*="section-"]');
      const productMedia = section && section.querySelector('product-media');
      if (!productMedia) return;

      const bind = () => {
        const main = productMedia.swiperMainInstance;
        if (!main) return false;
        const onClick = (swiper) => {
          const slide = swiper.clickedSlide;
          if (!slide) return;
          this.selectVariantFromIds(this.variantIdsOfSlide(slide));
        };
        main.on('click', onClick);
        if (productMedia.swiperThumbsInstance) productMedia.swiperThumbsInstance.on('click', onClick);
        this.galleryReady = true;
        this.renderSlotGallery(null, { initial: true });
        return true;
      };

      customElements.whenDefined('product-media').then(() => {
        if (bind()) return;
        let tries = 0;
        const timer = setInterval(() => {
          tries += 1;
          if (bind() || tries > 40) clearInterval(timer);
        }, 250);
      });
    }

    selectVariantFromIds(ids) {
      const visibleSlots = Array.from(this.slots).filter((slot) => slot.dataset.slotHidden !== 'true');
      for (const id of ids) {
        for (const slot of visibleSlots) {
          let variants = [];
          try {
            variants = JSON.parse(slot.dataset.variants || '[]');
          } catch {
            variants = [];
          }
          if (variants.some((v) => String(v.id) === id)) {
            if (String(slot.dataset.selectedVariantId) !== id) this.selectVariant(slot, id);
            this.slots.forEach((s) => s.classList.toggle('varianten-set__slot--gallery-active', s === slot));
            return;
          }
          if (String(slot.dataset.productId) === id) {
            this.slots.forEach((s) => s.classList.toggle('varianten-set__slot--gallery-active', s === slot));
            return;
          }
        }
      }
    }

    // --- ADD TO CART ---
    setupAtc() {
      if (!this.atcButton) return;

      this.atcButton.addEventListener('click', async () => {
        if (this.atcButton.disabled || this.atcButton.classList.contains('loading')) return;

        this.atcButton.classList.add('loading');
        this.atcButton.disabled = true;

        try {
          await this.addToCart();
        } catch (err) {
          console.error('Varianten-Set ATC error:', err);
        } finally {
          this.atcButton.classList.remove('loading');
          this.updateAvailability();
        }
      });
    }

    async addToCart() {
      const properties = {};

      this.slots.forEach((slot) => {
        const position = slot.dataset.slotPosition;
        const variantId = slot.dataset.selectedVariantId;
        if (position && variantId) {
          properties[`_slot_${position}`] = `gid://shopify/ProductVariant/${variantId}`;
        }
      });

      const body = {
        items: [
          {
            id: this.carrierVariantId,
            quantity: 1,
            properties,
          },
        ],
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (data.status === 422) {
        console.error('Cart add error:', data.description);
        return;
      }

      // Open cart drawer or redirect to cart
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        const sectionsResponse = await fetch(
          `${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`
        );
        const sections = await sectionsResponse.json();

        if (typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents({ sections });
          cartDrawer.open();
        } else {
          window.location.href = '/cart';
        }
      } else {
        window.location.href = '/cart';
      }
    }
  }

  customElements.define('varianten-set', VariantenSet);
}
